import React, { useState, useEffect, useMemo, FC } from 'react';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import { Select, Box, Button } from 'grommet';
import styled from 'styled-components';
import withLocation from '../../hocs/with-location';

const StyledSearch = styled(Box)`
  position: sticky;
  top: 70px;
  > * + * {
    ${props => (props.direction === 'row' ? `margin-left: 16px;` : `margin-top: 16px;`)}
  }
`;
const FullSearch: FC<any> = ({ search, current }) =>
{
  const data = useStaticQuery(graphql`
    query GET_FULL_SEARCH_DATA {
      hyper {
        myVehicleFuelTypes
        myVehicleBodyStyles
        myVehicleTransmissions
        myVehicleMakesModels {
          id
          name
          models {
            id
            name
          }
        }
      }
    }
  `);

  const selectedBlankMake = { name: 'Make', id: null };
  const selectedBlankModel = { name: 'Model', id: null };
  const selectedBlankMinPrice = { name: 'Min Price', value: null };
  const selectedBlankMaxPrice = { name: 'Max Price', value: null };
  const selectedBlankTrans = { name: 'Transmission', value: null };
  const selectedBlankFuel = { name: 'Fuel', value: null };
  const selectedBlankBody = { name: 'Body', value: null };
  const selectedBlankAll = { name: 'Any', id: null };

  const [selectedMake, setSelectedMake] = useState(selectedBlankMake);
  const [selectedModel, setSelectedModel] = useState(selectedBlankModel);
  const [selectedTransmission, setSelectedTransmission] = useState(selectedBlankTrans);
  const [selectedFueltype, setSelectedFueltype] = useState(selectedBlankFuel);
  const [selectedBodyStyle, setSelectedBodyStyle] = useState(selectedBlankBody);
  const [selectedPriceMin, setSelectedPriceMin] = useState(selectedBlankMinPrice);
  const [selectedPriceMax, setSelectedPriceMax] = useState(selectedBlankMaxPrice);
  const [models, setModels] = useState([selectedBlankModel]);

  const makes = data.hyper.myVehicleMakesModels;
  const fuelTypes = useMemo(() => data.hyper.myVehicleFuelTypes.map(item => ({ value: item, name: item })), [
    data.hyper.myVehicleFuelTypes,
  ]);
  const bodyStyles = useMemo(() => data.hyper.myVehicleBodyStyles.map(item => ({ value: item, name: item })), [
    data.hyper.myVehicleBodyStyles,
  ]);
  const transmissions = useMemo(() => data.hyper.myVehicleTransmissions.map(item => ({ value: item, name: item })), [
    data.hyper.myVehicleTransmissions,
  ]);

  const priceValues = useMemo(() =>
  {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    });

    const prices = [];

    // for (let i = 2000; i <= 20000; i += 1000) {

    let i = 0;

    while (i <= 60000) {
      if (i >= 15000) {
        i += 10000;
      } else {
        i += 1000;
      }

      const priceVals = { name: formatter.format(i).replace(/\D00$/, ''), value: `${i}` };
      prices.push(priceVals);
    }

    return prices;
  }, []);

  useEffect(() =>
  {
    if ((search && search.make) || (current && current.make)) {
      const searchedMakeId = search.make || current.make;
      const searchedMake = makes.filter(m => m.id === searchedMakeId);

      const make = searchedMake[0];
      if (make) {
        setSelectedMake(make);
        setModels([selectedBlankModel, ...make.models]);
      }

      if ((search && search.model) || (current && current.model)) {
        const searchedModelId = search.model || current.model;
        const searchedModel = make.models.filter(m => m.id === searchedModelId);

        if (searchedModel[0]) {
          setSelectedModel(searchedModel[0]);
        }
      }
    }

    if (search && search.priceFrom) {
      const searchedPrice = priceValues.filter(m => m.value === search.priceFrom);

      if (searchedPrice[0]) {
        setSelectedPriceMin(searchedPrice[0]);
      }
    }
    if (search && search.priceTo) {
      const searchedPrice = priceValues.filter(m => m.value === search.priceTo);
      if (searchedPrice[0]) {
        setSelectedPriceMax(searchedPrice[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, current]);

  const handleChangeMake = option =>
  {
    setSelectedMake(option);

    setModels([selectedBlankAll, ...option.models]);
    setSelectedModel(selectedBlankAll);
  };

  const handleSearch = () =>
  {
    // const _searchOptions = ['transmission', 'fuelType', 'bodyStyle'];
    const buildSearch = `${selectedMake && selectedMake.id ? `make=${selectedMake.id}` : ''}${
      selectedModel && selectedModel.id ? `&model=${selectedModel.id}` : ''
      }${selectedPriceMin && selectedPriceMin.value ? `&priceFrom=${selectedPriceMin.value}` : ''}${
      selectedPriceMax && selectedPriceMax.value ? `&priceTo=${selectedPriceMax.value}` : ''
      }${selectedTransmission && selectedTransmission.value ? `&transmission=${selectedTransmission.value}` : ''}${
      selectedBodyStyle && selectedBodyStyle.value ? `&bodyStyle=${selectedBodyStyle.value}` : ''
      }${selectedFueltype && selectedFueltype.value ? `&fuelType=${selectedFueltype.value}` : ''}`;

    navigate(`/search/?${buildSearch.replace(/^&/, '')}`);
  };
  return (
    <StyledSearch
      pad="large"
      direction="column"
      align="center"
      elevation="small"
      margin={{ bottom: 'large' }}
      background="light-1"
      animation="slideUp"
      round="small"
    >
      <Select
        options={[selectedBlankAll, ...makes]}
        labelKey="name"
        valueKey="id"
        value={selectedMake}
        placeholder="Make"
        onChange={({ option }) => handleChangeMake(option)}
      />
      <Select
        options={models}
        labelKey="name"
        valueKey="id"
        value={selectedModel}
        placeholder="Model"
        onChange={({ option }) => setSelectedModel(option)}
        disabled={!selectedMake}
      />
      <Select
        options={[selectedBlankMinPrice, ...priceValues]}
        labelKey="name"
        valueKey="value"
        value={selectedPriceMin}
        placeholder="Price From"
        onChange={({ option }) => setSelectedPriceMin(option)}
      />
      <Select
        options={[selectedBlankMaxPrice, ...priceValues]}
        labelKey="name"
        valueKey="value"
        value={selectedPriceMax}
        placeholder="Price To"
        onChange={({ option }) => setSelectedPriceMax(option)}
      />
      <Select
        options={[selectedBlankTrans, ...transmissions]}
        placeholder="Transmission"
        value={selectedTransmission}
        labelKey="name"
        valueKey="value"
        onChange={({ option }) => setSelectedTransmission(option)}
      />
      <Select
        options={[selectedBlankFuel, ...fuelTypes]}
        placeholder="Fuel type"
        value={selectedFueltype}
        labelKey="name"
        valueKey="value"
        onChange={({ option }) => setSelectedFueltype(option)}
      />
      <Select
        options={[selectedBlankBody, ...bodyStyles]}
        placeholder="Body style"
        value={selectedBodyStyle}
        labelKey="name"
        valueKey="value"
        onChange={({ option }) => setSelectedBodyStyle(option)}
      />

      <Button label="Search" onClick={() => handleSearch()} />
    </StyledSearch>
  );
};

export default withLocation(FullSearch);
